import { CodedError, type Locale, type InterestTopic } from '@ebu/contracts';
import {
  parseWorld,
  parseWorldFile,
  serializeWorld,
  toImportError,
  validateWorld,
  type World,
  type WorldParseFailure,
} from '@ebu/world-schema';
import { sanitizeFileName } from '../../diagnostics/download';
import { openDatabase, requestToPromise, txDone } from '../idb';
import {
  LOCAL_DB_NAME,
  LOCAL_DB_VERSION,
  STORES,
  applyUpgrades,
  attemptRecordSchema,
  evidenceRecordSchema,
  projectRecordSchema,
  workspaceRecordSchema,
  type AttemptStoreRecord,
  type EvidenceStoreRecord,
  type ProjectRecord,
  type WorkspaceRecord,
} from './schema';

/**
 * Browser-only persistence for guest workspaces and world projects (IndexedDB, versioned).
 *
 * Guarantees:
 * - every write is validated first (WORLD-SAVE-001) and failures to write, including quota,
 *   surface as WORLD-SAVE-002 with the previous saved version left intact;
 * - unreadable records are reported (WORLD-LOAD-001), never silently discarded; the raw record can
 *   be exported and deleting it is always an explicit call;
 * - imports never overwrite an existing project (a clashing id gets a fresh one);
 * - projects belong to one workspace and are unaffected by locale/broadcaster changes.
 * Errors are thrown as CodedError; the calling UI logs them and shows the coded message.
 */

export interface LocalStoreOptions {
  idbFactory: IDBFactory | undefined;
  dbName?: string;
  clock?: () => Date;
  randomId?: () => string;
}

export interface ProjectSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  objectCount: number;
}

export type CorruptReason = WorldParseFailure | 'bad-record';

export interface CorruptProject {
  key: string;
  reason: CorruptReason;
}

export interface ExportFile {
  fileName: string;
  text: string;
}

const PROJECT_FILE_SUFFIX = '.kidsworld.json';

function defaultRandomId(): string {
  return globalThis.crypto.randomUUID();
}

function isQuotaError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'QuotaExceededError'
  );
}

function summarise(world: World): ProjectSummary {
  return {
    id: world.id,
    title: world.title,
    createdAt: world.createdAt,
    updatedAt: world.updatedAt,
    objectCount: world.objects.length,
  };
}

function oldestGuest(rawRecords: unknown[]): WorkspaceRecord | null {
  return (
    rawRecords
      .map((r) => workspaceRecordSchema.safeParse(r))
      .flatMap((r) => (r.success && r.data.kind === 'guest' ? [r.data] : []))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0] ?? null
  );
}

type ReadResult =
  | { ok: true; record: ProjectRecord; world: World; migratedFrom: number | null }
  | { ok: false; reason: CorruptReason };

function readProjectRecord(raw: unknown): ReadResult {
  const envelope = projectRecordSchema.safeParse(raw);
  if (!envelope.success) return { ok: false, reason: 'bad-record' };
  const parsed = parseWorld(envelope.data.world);
  if (!parsed.ok) return { ok: false, reason: parsed.reason };
  if (parsed.world.id !== envelope.data.id) return { ok: false, reason: 'bad-record' };
  return {
    ok: true,
    record: envelope.data,
    world: parsed.world,
    migratedFrom: parsed.migratedFrom,
  };
}

export class LocalStore {
  private constructor(
    private db: IDBDatabase,
    private readonly clock: () => Date,
    private readonly randomId: () => string,
  ) {}

  /** Opens (and if needed upgrades) the local database. Throws WEB-STORAGE-001 when unavailable. */
  static async open(options: LocalStoreOptions): Promise<LocalStore> {
    const factory = options.idbFactory;
    try {
      if (!factory) throw new Error('IndexedDB is not available in this browser');
      const db = await openDatabase(
        factory,
        options.dbName ?? LOCAL_DB_NAME,
        LOCAL_DB_VERSION,
        (database, tx, oldVersion) => applyUpgrades(database, tx, oldVersion, LOCAL_DB_VERSION),
      );
      // Let a newer version in another tab upgrade instead of being blocked by this connection.
      db.onversionchange = () => db.close();
      return new LocalStore(
        db,
        options.clock ?? (() => new Date()),
        options.randomId ?? defaultRandomId,
      );
    } catch (cause) {
      throw new CodedError('WEB-STORAGE-001', 'Local project storage could not be opened', {
        cause,
        context: { err: cause },
      });
    }
  }

  // ------------------------------------------------------------------ workspace

  /** The existing guest workspace, or null. Never writes, so a first visit stores nothing. */
  async getGuestWorkspace(): Promise<WorkspaceRecord | null> {
    const tx = this.db.transaction(STORES.workspace, 'readonly');
    const all = (await requestToPromise(tx.objectStore(STORES.workspace).getAll())) as unknown[];
    return oldestGuest(all);
  }

  /** The browser's guest workspace, created on first use. Stores no personal data. */
  async getOrCreateGuestWorkspace(locale: Locale): Promise<WorkspaceRecord> {
    const tx = this.db.transaction(STORES.workspace, 'readwrite');
    const store = tx.objectStore(STORES.workspace);
    const existing = oldestGuest((await requestToPromise(store.getAll())) as unknown[]);
    if (existing) {
      await txDone(tx);
      return existing;
    }
    const record: WorkspaceRecord = workspaceRecordSchema.parse({
      id: this.randomId(),
      kind: 'guest',
      sampleProfileId: null,
      createdAt: this.clock().toISOString(),
      locale,
      interests: null,
    });
    store.add(record);
    await this.commit(tx, 'WEB-STORAGE-001', 'Could not create the guest workspace');
    return record;
  }

  /** Locale, broadcaster and opt-in interests only; never touches the workspace's projects. */
  async updateWorkspace(
    id: string,
    patch: {
      locale?: Locale;
      interests?: InterestTopic[] | null;
      broadcasterId?: string | null;
      graphics?: 'world' | 'map';
      saveProgress?: boolean | null;
      startChoice?: 'beginning' | 'harder' | null;
      activeSampleId?: string | null;
      ageMode?: 'little' | 'bigger' | null;
      screenTime?: WorkspaceRecord['screenTime'];
    },
  ): Promise<WorkspaceRecord> {
    const tx = this.db.transaction(STORES.workspace, 'readwrite');
    const store = tx.objectStore(STORES.workspace);
    const current = workspaceRecordSchema.parse(await requestToPromise(store.get(id)));
    const next = workspaceRecordSchema.parse({ ...current, ...patch });
    store.put(next);
    await this.commit(tx, 'WEB-STORAGE-001', 'Could not update the workspace');
    return next;
  }

  /** A workspace by id (guest or sample learner), or null if it does not exist or is unreadable. */
  async getWorkspace(id: string): Promise<WorkspaceRecord | null> {
    const raw: unknown = await requestToPromise(
      this.db.transaction(STORES.workspace, 'readonly').objectStore(STORES.workspace).get(id),
    );
    const parsed = workspaceRecordSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
  }

  /** Fictional sample learner workspaces cloned into this browser, oldest first. */
  async listSampleWorkspaces(): Promise<WorkspaceRecord[]> {
    const tx = this.db.transaction(STORES.workspace, 'readonly');
    const all = (await requestToPromise(tx.objectStore(STORES.workspace).getAll())) as unknown[];
    return all
      .map((r) => workspaceRecordSchema.safeParse(r))
      .flatMap((r) => (r.success && r.data.kind === 'sample-learner' ? [r.data] : []))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  /**
   * Clones a fictional sample learner into a new local workspace in one transaction (all or
   * nothing): the workspace record, its seeded attempts and evidence, and its seeded projects
   * (validated like any save). Visitors therefore never write into a shared record.
   */
  async cloneSampleWorkspace(seed: {
    sampleProfileId: string;
    locale: Locale;
    startChoice: 'beginning' | 'harder';
    /** The seeded attempts and evidence, keyed to the new workspace. */
    learning: (workspaceId: string) => {
      attempts: readonly AttemptStoreRecord[];
      evidence: readonly EvidenceStoreRecord[];
    };
    worlds: readonly World[];
  }): Promise<WorkspaceRecord> {
    const workspace: WorkspaceRecord = workspaceRecordSchema.parse({
      id: this.randomId(),
      kind: 'sample-learner',
      sampleProfileId: seed.sampleProfileId,
      createdAt: this.clock().toISOString(),
      locale: seed.locale,
      interests: null,
      saveProgress: true,
      startChoice: seed.startChoice,
    });
    const learning = seed.learning(workspace.id);
    const attempts = learning.attempts.map((a) => attemptRecordSchema.parse(a));
    const evidence = learning.evidence.map((e) => evidenceRecordSchema.parse(e));
    if ([...attempts, ...evidence].some((r) => r.workspaceId !== workspace.id)) {
      throw new CodedError('WEB-STORAGE-001', 'Sample records belong to another workspace', {
        context: { reason: 'other-workspace' },
      });
    }
    const projects = seed.worlds.map((world) => {
      const checked = validateWorld({ ...world, id: this.randomId() });
      if (!checked.ok) {
        throw new CodedError('WORLD-SAVE-001', 'Sample project failed validation', {
          context: { reason: checked.reason, issuePaths: checked.issues },
        });
      }
      return projectRecordSchema.parse({
        id: checked.world.id,
        workspaceId: workspace.id,
        updatedAt: checked.world.updatedAt,
        world: checked.world,
      });
    });
    const names = [STORES.workspace, STORES.project, STORES.attempt, STORES.evidence];
    const tx = this.db.transaction(names, 'readwrite');
    try {
      tx.objectStore(STORES.workspace).add(workspace);
      for (const r of projects) tx.objectStore(STORES.project).add(r);
      for (const r of attempts) tx.objectStore(STORES.attempt).add(r);
      for (const r of evidence) tx.objectStore(STORES.evidence).add(r);
    } catch (cause) {
      tx.abort();
      throw new CodedError('WEB-STORAGE-001', 'Could not create the sample learner workspace', {
        cause,
        context: { quotaExceeded: isQuotaError(cause), err: cause },
      });
    }
    await this.commit(tx, 'WEB-STORAGE-001', 'Could not create the sample learner workspace');
    return workspace;
  }

  /**
   * Removes a sample learner workspace with all its projects, attempts and evidence. The guest
   * workspace is never removed this way ("Clear my data" is the only path for it).
   */
  async deleteSampleWorkspace(id: string): Promise<void> {
    const existing = await this.getWorkspace(id);
    if (!existing || existing.kind !== 'sample-learner') return;
    const names = [STORES.workspace, STORES.project, STORES.attempt, STORES.evidence];
    const tx = this.db.transaction(names, 'readwrite');
    tx.objectStore(STORES.workspace).delete(id);
    for (const name of [STORES.project, STORES.attempt, STORES.evidence]) {
      const store = tx.objectStore(name);
      const keys = await requestToPromise(store.index('by-workspace').getAllKeys(id));
      for (const key of keys) store.delete(key);
    }
    await this.commit(tx, 'WEB-STORAGE-001', 'Could not remove the sample learner workspace');
  }

  // ------------------------------------------------------------------ projects

  async listProjects(
    workspaceId: string,
  ): Promise<{ projects: ProjectSummary[]; corrupt: CorruptProject[] }> {
    const tx = this.db.transaction(STORES.project, 'readonly');
    const store = tx.objectStore(STORES.project);
    const [keys, values] = await Promise.all([
      requestToPromise(store.getAllKeys()),
      requestToPromise(store.getAll()) as Promise<unknown[]>,
    ]);
    const projects: ProjectSummary[] = [];
    const corrupt: CorruptProject[] = [];
    values.forEach((raw, i) => {
      const owner = (raw as { workspaceId?: unknown } | null)?.workspaceId;
      // Records of other workspaces are not this workspace's business; unowned ones are reported.
      if (typeof owner === 'string' && owner !== workspaceId) return;
      const result = readProjectRecord(raw);
      if (result.ok) projects.push(summarise(result.world));
      else corrupt.push({ key: String(keys[i]), reason: result.reason });
    });
    projects.sort(
      (a, b) => b.updatedAt.localeCompare(a.updatedAt) || a.title.localeCompare(b.title),
    );
    return { projects, corrupt };
  }

  /** The readable worlds of a workspace (for previews in My Projects); unreadable ones are skipped. */
  async listProjectWorlds(workspaceId: string): Promise<World[]> {
    const raw = (await requestToPromise(
      this.db
        .transaction(STORES.project, 'readonly')
        .objectStore(STORES.project)
        .index('by-workspace')
        .getAll(workspaceId),
    )) as unknown[];
    return raw.flatMap((r) => {
      const result = readProjectRecord(r);
      return result.ok ? [result.world] : [];
    });
  }

  /** Returns null for a project that no longer exists; throws WORLD-LOAD-001 for unreadable ones. */
  async loadProject(id: string): Promise<{ world: World; migratedFrom: number | null } | null> {
    const raw: unknown = await requestToPromise(
      this.db.transaction(STORES.project, 'readonly').objectStore(STORES.project).get(id),
    );
    if (raw === undefined) return null;
    const result = readProjectRecord(raw);
    if (!result.ok) {
      throw new CodedError('WORLD-LOAD-001', 'Saved project is unreadable', {
        context: { projectId: id, reason: result.reason },
      });
    }
    return { world: result.world, migratedFrom: result.migratedFrom };
  }

  /** Validates, stamps updatedAt and writes. The previous version is kept if anything fails. */
  async saveProject(workspaceId: string, world: World): Promise<ProjectSummary> {
    const now = this.clock().toISOString();
    const stamped = { ...world, updatedAt: now < world.createdAt ? world.createdAt : now };
    const checked = validateWorld(stamped);
    if (!checked.ok) {
      throw new CodedError('WORLD-SAVE-001', 'Project failed validation before saving', {
        context: { reason: checked.reason, issuePaths: checked.issues },
      });
    }
    const record: ProjectRecord = {
      id: checked.world.id,
      workspaceId,
      updatedAt: checked.world.updatedAt,
      world: checked.world,
    };

    const tx = this.db.transaction(STORES.project, 'readwrite');
    const store = tx.objectStore(STORES.project);
    let existing: { workspaceId?: unknown } | undefined;
    try {
      existing = (await requestToPromise(store.get(record.id))) as typeof existing;
    } catch (cause) {
      throw this.saveFailure(cause, record.id);
    }
    if (existing && existing.workspaceId !== workspaceId) {
      tx.abort();
      throw new CodedError('WORLD-SAVE-001', 'Project id belongs to another workspace', {
        context: { reason: 'other-workspace', projectId: record.id },
      });
    }
    try {
      store.put(record);
    } catch (cause) {
      // Synchronous put failures (e.g. quota on some engines) still leave the old record intact.
      tx.abort();
      throw this.saveFailure(cause, record.id);
    }
    await this.commit(tx, 'WORLD-SAVE-002', 'Could not write the project', record.id);
    return summarise(checked.world);
  }

  /** Explicit deletion (also the only way a corrupt record is ever removed). */
  async deleteProject(key: string): Promise<void> {
    const tx = this.db.transaction(STORES.project, 'readwrite');
    tx.objectStore(STORES.project).delete(key);
    await this.commit(tx, 'WORLD-SAVE-002', 'Could not delete the project', key);
  }

  async exportProject(id: string): Promise<ExportFile | null> {
    const loaded = await this.loadProject(id);
    if (!loaded) return null;
    return {
      fileName: `${sanitizeFileName(loaded.world.title, 'project')}${PROJECT_FILE_SUFFIX}`,
      text: serializeWorld(loaded.world),
    };
  }

  /** Rescue path for unreadable records: the stored value as JSON, for inspection or support. */
  async exportRawRecord(key: string): Promise<ExportFile | null> {
    const raw: unknown = await requestToPromise(
      this.db.transaction(STORES.project, 'readonly').objectStore(STORES.project).get(key),
    );
    if (raw === undefined) return null;
    return {
      fileName: `unreadable-project-${sanitizeFileName(key, 'record')}.json`,
      text: `${JSON.stringify(raw, null, 2)}\n`,
    };
  }

  /**
   * Imports a project file into the workspace. Throws WORLD-IMPORT-003 for invalid files (nothing
   * written). Never overwrites: a clashing id is replaced with a fresh one.
   */
  async importProject(
    workspaceId: string,
    text: string,
  ): Promise<{ summary: ProjectSummary; migratedFrom: number | null; idChanged: boolean }> {
    const parsed = parseWorldFile(text);
    if (!parsed.ok) throw toImportError(parsed);
    const clash =
      (await requestToPromise(
        this.db
          .transaction(STORES.project, 'readonly')
          .objectStore(STORES.project)
          .count(parsed.world.id),
      )) > 0;
    const world = clash ? { ...parsed.world, id: this.randomId() } : parsed.world;
    const summary = await this.saveProject(workspaceId, world);
    return { summary, migratedFrom: parsed.migratedFrom, idChanged: clash };
  }

  // ------------------------------------------------------------------ learning evidence (Phase 7)

  /**
   * Stores one finished attempt and the recomputed evidence in one transaction (both or neither).
   * Failures, including a full quota, are LEARN-EVIDENCE-001; play continues in memory.
   */
  async saveLearning(
    attempt: AttemptStoreRecord,
    evidence: readonly EvidenceStoreRecord[],
  ): Promise<void> {
    const parsedAttempt = attemptRecordSchema.safeParse(attempt);
    const parsedEvidence = evidence.map((e) => evidenceRecordSchema.safeParse(e));
    if (!parsedAttempt.success || parsedEvidence.some((e) => !e.success)) {
      throw new CodedError('LEARN-EVIDENCE-001', 'Learning record failed validation', {
        context: { reason: 'invalid-record' },
      });
    }
    const tx = this.db.transaction([STORES.attempt, STORES.evidence], 'readwrite');
    try {
      tx.objectStore(STORES.attempt).put(parsedAttempt.data);
      for (const e of parsedEvidence) if (e.success) tx.objectStore(STORES.evidence).put(e.data);
    } catch (cause) {
      // A full quota can fail synchronously; nothing of this attempt is kept (all or nothing).
      tx.abort();
      throw new CodedError('LEARN-EVIDENCE-001', 'Could not save the challenge attempt', {
        cause,
        context: { quotaExceeded: isQuotaError(cause), err: cause },
      });
    }
    await this.commit(tx, 'LEARN-EVIDENCE-001', 'Could not save the challenge attempt');
  }

  /** Saved attempts of a workspace, oldest first; unreadable records are counted, never deleted. */
  async listAttempts(
    workspaceId: string,
  ): Promise<{ attempts: AttemptStoreRecord[]; unreadable: number }> {
    const tx = this.db.transaction(STORES.attempt, 'readonly');
    const raw = (await requestToPromise(
      tx.objectStore(STORES.attempt).index('by-workspace').getAll(workspaceId),
    )) as unknown[];
    const attempts: AttemptStoreRecord[] = [];
    let unreadable = 0;
    for (const record of raw) {
      const parsed = attemptRecordSchema.safeParse(record);
      if (parsed.success) attempts.push(parsed.data);
      else unreadable += 1;
    }
    attempts.sort((a, b) => a.endedAt.localeCompare(b.endedAt) || a.id.localeCompare(b.id));
    return { attempts, unreadable };
  }

  async listEvidence(workspaceId: string): Promise<EvidenceStoreRecord[]> {
    const tx = this.db.transaction(STORES.evidence, 'readonly');
    const raw = (await requestToPromise(
      tx.objectStore(STORES.evidence).index('by-workspace').getAll(workspaceId),
    )) as unknown[];
    return raw.flatMap((r) => {
      const parsed = evidenceRecordSchema.safeParse(r);
      return parsed.success ? [parsed.data] : [];
    });
  }

  /** "No thanks" after saving earlier: removes this workspace's attempts and evidence only. */
  async deleteLearning(workspaceId: string): Promise<void> {
    const names = [STORES.attempt, STORES.evidence];
    const tx = this.db.transaction(names, 'readwrite');
    for (const name of names) {
      const store = tx.objectStore(name);
      const keys = await requestToPromise(store.index('by-workspace').getAllKeys(workspaceId));
      for (const key of keys) store.delete(key);
    }
    await this.commit(tx, 'LEARN-EVIDENCE-001', 'Could not delete saved progress');
  }

  // ------------------------------------------------------------------ housekeeping

  /** "Clear my data": removes every workspace, project, attempt and evidence record in this browser. */
  async clearAllLocalData(): Promise<void> {
    const names = Object.values(STORES);
    const tx = this.db.transaction(names, 'readwrite');
    for (const name of names) tx.objectStore(name).clear();
    await this.commit(tx, 'WEB-STORAGE-001', 'Could not clear local data');
  }

  close(): void {
    this.db.close();
  }

  private saveFailure(cause: unknown, projectId: string): CodedError {
    return new CodedError('WORLD-SAVE-002', 'Could not write the project', {
      cause,
      context: { projectId, quotaExceeded: isQuotaError(cause), err: cause },
    });
  }

  private async commit(
    tx: IDBTransaction,
    code: 'WORLD-SAVE-002' | 'WEB-STORAGE-001' | 'LEARN-EVIDENCE-001',
    message: string,
    projectId?: string,
  ): Promise<void> {
    try {
      await txDone(tx);
    } catch (cause) {
      if (code === 'WORLD-SAVE-002') throw this.saveFailure(cause, projectId ?? '');
      throw new CodedError(code, message, {
        cause,
        context: { quotaExceeded: isQuotaError(cause), err: cause },
      });
    }
  }
}
